import { useId, useRef, type ReactNode, type PointerEvent, type KeyboardEvent } from 'react';
import {
  motion,
  useMotionValue,
  useMotionTemplate,
  useSpring,
  useReducedMotion,
} from 'motion/react';
import { ArrowUpRight, Inbox, type LucideIcon } from 'lucide-react';
import { initials } from '../lib/api';
import './luxury-motion.css';
export function PageHeader({
  eyebrow,
  title,
  description,
  action,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="page-heading">
      <div>
        {eyebrow && <div className="eyebrow">{eyebrow}</div>}
        <h1>{title}</h1>
        {description && <p>{description}</p>}
      </div>
      {action && <div className="heading-actions">{action}</div>}
    </div>
  );
}
export function Panel({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <section className={`panel ${className}`}>{children}</section>;
}
export function Badge({
  children,
  tone = 'gray',
  dot = false,
}: {
  children: ReactNode;
  tone?: 'green' | 'amber' | 'red' | 'gray' | 'blue';
  dot?: boolean;
}) {
  return (
    <span className={`badge badge-${tone}`}>
      {dot && <i />}
      {children}
    </span>
  );
}
export function Avatar({
  name,
  size = 36,
  image,
}: {
  name: string;
  size?: number;
  image?: string;
}) {
  return (
    <span
      className={`avatar avatar-${(name?.charCodeAt(0) || 0) % 5}`}
      style={{ width: size, height: size, fontSize: size / 3 }}
    >
      {image ? <img src={image} alt={name} /> : initials(name)}
    </span>
  );
}
export function EmptyState({
  icon: Icon = Inbox,
  title,
  description,
  action,
}: {
  icon?: LucideIcon;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty-state">
      <span className="empty-icon">
        <Icon size={27} />
      </span>
      <h3>{title}</h3>
      {description && <p>{description}</p>}
      {action}
    </div>
  );
}
export function Tabs({
  items,
  value,
  onChange,
  ariaLabel = 'Filter options',
}: {
  items: string[];
  value: string;
  onChange: (value: string) => void;
  ariaLabel?: string;
}) {
  const id = useId();
  const reducedMotion = useReducedMotion();
  const buttons = useRef<(HTMLButtonElement | null)[]>([]);
  const selectedIndex = Math.max(0, items.indexOf(value));
  function navigate(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    let next: number;
    if (event.key === 'ArrowRight') next = (index + 1) % items.length;
    else if (event.key === 'ArrowLeft') next = (index + items.length - 1) % items.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = items.length - 1;
    else return;
    event.preventDefault();
    onChange(items[next]);
    buttons.current[next]?.focus();
  }
  return (
    <div className="smooth-tabs luxury-tabs" role="group" aria-label={ariaLabel}>
      {items.map((item, index) => (
        <motion.button
          key={item}
          ref={(node) => {
            buttons.current[index] = node;
          }}
          type="button"
          aria-pressed={value === item}
          tabIndex={index === selectedIndex ? 0 : -1}
          className={item === value ? 'active' : ''}
          onClick={() => onChange(item)}
          onKeyDown={(event) => navigate(event, index)}
          whileTap={reducedMotion ? undefined : { scale: 0.98 }}
        >
          {item === value && (
            <motion.span
              className="tab-pill"
              aria-hidden="true"
              layoutId={reducedMotion ? undefined : `tab-${id}`}
              transition={{ type: 'spring', stiffness: 420, damping: 36, mass: 0.8 }}
              style={{ borderRadius: 9 }}
            />
          )}
          <span>{item}</span>
        </motion.button>
      ))}
    </div>
  );
}
export function StatCard({
  label,
  value,
  icon: Icon,
  detail,
  trend,
}: {
  label: string;
  value: ReactNode;
  icon?: LucideIcon;
  detail?: ReactNode;
  trend?: ReactNode;
}) {
  const reducedMotion = useReducedMotion();
  const pointerX = useMotionValue(50);
  const pointerY = useMotionValue(0);
  const tiltX = useSpring(0, { stiffness: 190, damping: 24, mass: 0.7 });
  const tiltY = useSpring(0, { stiffness: 190, damping: 24, mass: 0.7 });
  const lift = useSpring(0, { stiffness: 210, damping: 25 });
  const shine = useSpring(0, { stiffness: 170, damping: 24 });
  const light = useMotionTemplate`radial-gradient(ellipse at ${pointerX}% ${pointerY}%, rgba(255,255,255,.86), rgba(101,139,255,.10) 35%, transparent 72%)`;
  function move(event: PointerEvent<HTMLElement>) {
    if (
      reducedMotion ||
      event.pointerType === 'touch' ||
      !window.matchMedia('(hover: hover) and (pointer: fine)').matches
    )
      return;
    const rect = event.currentTarget.getBoundingClientRect();
    const x = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
    const y = Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height));
    pointerX.set(x * 100);
    pointerY.set(y * 100);
    tiltX.set((0.5 - y) * 5);
    tiltY.set((x - 0.5) * 5);
    lift.set(-3);
    shine.set(0.75);
  }
  function reset() {
    tiltX.set(0);
    tiltY.set(0);
    lift.set(0);
    shine.set(0);
  }
  return (
    <motion.article
      className="stat-card luxury-stat"
      aria-label={label}
      onPointerMove={move}
      onPointerLeave={reset}
      onPointerCancel={reset}
      style={{
        rotateX: reducedMotion ? 0 : tiltX,
        rotateY: reducedMotion ? 0 : tiltY,
        y: reducedMotion ? 0 : lift,
        transformPerspective: 900,
      }}
    >
      <motion.span
        className="stat-specular"
        aria-hidden="true"
        style={{ background: light, opacity: reducedMotion ? 0 : shine }}
      />
      <span className="stat-edge" aria-hidden="true" />
      <div className="stat-top">
        <span>{label}</span>
        {Icon && (
          <span className="stat-icon">
            <Icon size={18} />
          </span>
        )}
      </div>
      <div className="stat-value">{value}</div>
      <div className="stat-foot">
        {trend && (
          <span className="stat-trend">
            <ArrowUpRight size={12} />
            {trend}
          </span>
        )}
        {detail && <span>{detail}</span>}
      </div>
    </motion.article>
  );
}
export function SectionHeading({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: string;
  action?: ReactNode;
}) {
  return (
    <div className="section-heading">
      <div>
        <h2>{title}</h2>
        {subtitle && <p>{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}
export function Score({ value }: { value: number }) {
  return (
    <span className={`score ${value >= 85 ? 'high' : value >= 70 ? 'medium' : 'low'}`}>
      <span className="score-dot" />
      {Math.round(value)}
    </span>
  );
}
export function Skeleton() {
  return (
    <div className="skeleton-page">
      <div className="skeleton sk-heading" />
      <div className="stats-grid">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="skeleton sk-stat" />
        ))}
      </div>
      <div className="skeleton sk-panel" />
      <div className="skeleton sk-panel" />
    </div>
  );
}
